<template>
  <div>
    <!-- Header -->
    <v-app-bar
      app
      color="white"
      elevation="1"
      height="80"
    >
      <v-container class="d-flex align-center">
        <v-toolbar-title class="d-flex align-center">
          <v-icon size="40" color="primary" class="mr-3">
            mdi-store
          </v-icon>
          <span class="headline font-weight-bold">AI-POS</span>
        </v-toolbar-title>
        
        <v-spacer></v-spacer>
        
        <v-btn text @click="navigateTo('/home')">Inicio</v-btn>
        <v-btn text @click="scrollToSection('features')">Características</v-btn>
        <v-btn text @click="scrollToSection('demo')">Demo</v-btn>
        
        <v-btn
          color="primary"
          class="ml-4"
          @click="navigateTo('/Login')"
        >
          <v-icon left>mdi-login</v-icon>
          Iniciar Sesión
        </v-btn>
      </v-container>
    </v-app-bar>

    <!-- Hero Section with Screenshots -->
    <section class="hero-demo py-16">
      <v-container>
        <v-row align="center">
          <v-col cols="12" md="6">
            <h1 class="display-1 font-weight-bold mb-4">
              Sistema ERP AI-POS en Acción
            </h1>
            <p class="title font-weight-light mb-6">
              Descubre cómo nuestro sistema revoluciona la gestión de tu negocio 
              con interfaces intuitivas y funcionalidades avanzadas.
            </p>
            
            <v-list class="transparent">
              <v-list-item
                v-for="highlight in demoHighlights"
                :key="highlight.title"
                class="px-0"
              >
                <v-list-item-avatar>
                  <v-icon :color="highlight.color">{{ highlight.icon }}</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold">
                    {{ highlight.title }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ highlight.description }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            
            <div class="mt-6">
              <v-btn
                x-large
                color="primary"
                class="mr-4"
                @click="startLiveDemo"
              >
                <v-icon left>mdi-play-circle</v-icon>
                Demo en Vivo
              </v-btn>
              <v-btn
                x-large
                outlined
                color="primary"
                @click="scheduleDemo"
              >
                <v-icon left>mdi-calendar</v-icon>
                Agendar Demo
              </v-btn>
            </div>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-card elevation="8" class="demo-card">
              <div class="demo-mockup">
                <div class="mockup-header">
                  <span class="mockup-title">AI-POS Dashboard</span>
                </div>
                <div class="mockup-content">
                  <div class="mockup-stats">
                    <div class="stat-card green">
                      <div class="stat-title">Ventas Hoy</div>
                      <div class="stat-value">€2,450.75</div>
                    </div>
                    <div class="stat-card blue">
                      <div class="stat-title">Facturas</div>
                      <div class="stat-value">127</div>
                    </div>
                    <div class="stat-card orange">
                      <div class="stat-title">Stock</div>
                      <div class="stat-value">8,542</div>
                    </div>
                  </div>
                  <div class="mockup-table">
                    <div class="table-header">Últimas Transacciones</div>
                    <div class="table-row">10:30 - FE001234 - María García - €125.50</div>
                    <div class="table-row">10:25 - FE001233 - Consumidor Final - €45.00</div>
                    <div class="table-row">10:22 - NC001 - Juan Pérez - -€25.75</div>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Feature Screenshots -->
    <section id="features" class="grey lighten-5 py-16">
      <v-container>
        <v-row>
          <v-col cols="12" class="text-center mb-8">
            <h2 class="display-1 font-weight-bold mb-4">
              Funcionalidades del Sistema
            </h2>
            <p class="title font-weight-light">
              Explora las diferentes características en acción
            </p>
          </v-col>
        </v-row>

        <v-row>
          <v-col
            v-for="(feature, index) in systemFeatures"
            :key="index"
            cols="12"
            md="6"
            class="mb-8"
          >
            <v-card elevation="4" class="feature-demo-card h-100">
              <v-card-title class="justify-center">
                <v-icon :color="feature.color" size="40" class="mr-3">
                  {{ feature.icon }}
                </v-icon>
                {{ feature.title }}
              </v-card-title>
              
              <v-card-text>
                <p class="body-1 mb-4">{{ feature.description }}</p>
                
                <v-chip
                  v-for="tag in feature.features"
                  :key="tag"
                  small
                  class="ma-1"
                  :color="feature.color"
                  outlined
                >
                  {{ tag }}
                </v-chip>
                
                <div class="text-center mt-4">
                  <v-btn
                    :color="feature.color"
                    @click="navigateTo(feature.route)"
                  >
                    <v-icon left>mdi-eye</v-icon>
                    Ver en Acción
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Live Demo Section -->
    <section id="demo" class="py-16">
      <v-container>
        <v-row>
          <v-col cols="12" class="text-center mb-8">
            <h2 class="display-1 font-weight-bold mb-4">
              Prueba el Sistema en Vivo
            </h2>
            <p class="title font-weight-light">
              Interactúa con una demo completamente funcional
            </p>
          </v-col>
        </v-row>

        <v-row justify="center">
          <v-col cols="12" md="10">
            <v-card elevation="8" class="demo-interactive">
              <v-card-title class="justify-center primary white--text">
                <v-icon left color="white">mdi-monitor-dashboard</v-icon>
                Demo Interactiva - Acceso Completo
              </v-card-title>
              
              <v-card-text class="pa-8">
                <v-row>
                  <v-col
                    v-for="demo in demoModules"
                    :key="demo.name"
                    cols="12"
                    sm="6"
                    md="3"
                    class="mb-4"
                  >
                    <v-card
                      :color="demo.color"
                      dark
                      hover
                      class="demo-module-card"
                      @click="navigateTo(demo.route)"
                    >
                      <v-card-text class="text-center pa-6">
                        <v-icon size="50" class="mb-3">{{ demo.icon }}</v-icon>
                        <div class="headline font-weight-bold">{{ demo.name }}</div>
                        <div class="caption mt-2">{{ demo.description }}</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
                
                <v-divider class="my-6"></v-divider>
                
                <div class="text-center">
                  <v-alert
                    type="info"
                    outlined
                    class="mb-4"
                  >
                    <strong>Datos de prueba incluidos:</strong> productos, clientes, facturas y transacciones de ejemplo para una experiencia completa
                  </v-alert>
                  
                  <v-btn
                    x-large
                    color="success"
                    class="mt-4"
                    @click="navigateTo('/Login')"
                  >
                    <v-icon left>mdi-login</v-icon>
                    Acceder a la Demo Completa
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Benefits Section -->
    <section class="primary white--text py-16">
      <v-container>
        <v-row>
          <v-col cols="12" class="text-center mb-8">
            <h2 class="display-1 font-weight-bold mb-4">
              Ventajas Competitivas
            </h2>
            <p class="title font-weight-light">
              Por qué AI-POS es la mejor opción para tu negocio
            </p>
          </v-col>
        </v-row>

        <v-row>
          <v-col
            v-for="benefit in competitiveAdvantages"
            :key="benefit.title"
            cols="12"
            md="4"
            class="mb-4"
          >
            <div class="text-center">
              <v-avatar size="80" color="white" class="mb-4">
                <v-icon :color="benefit.color" size="40">{{ benefit.icon }}</v-icon>
              </v-avatar>
              <h3 class="headline font-weight-bold mb-3">
                {{ benefit.title }}
              </h3>
              <p class="body-1">
                {{ benefit.description }}
              </p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- CTA Section -->
    <section class="py-16">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="8" class="text-center">
            <h2 class="display-1 font-weight-bold mb-4">
              ¿Listo para Transformar tu Negocio?
            </h2>
            <p class="title font-weight-light mb-8">
              Únete a cientos de empresas que ya transformaron su gestión con AI-POS
            </p>
            
            <div class="d-flex flex-wrap justify-center">
              <v-btn
                x-large
                color="primary"
                class="ma-2"
                @click="navigateTo('/Login')"
              >
                <v-icon left>mdi-rocket-launch</v-icon>
                Comenzar Gratis
              </v-btn>
              <v-btn
                x-large
                outlined
                color="primary"
                class="ma-2"
                @click="contactSales"
              >
                <v-icon left>mdi-phone</v-icon>
                Hablar con Ventas
              </v-btn>
              <v-btn
                x-large
                outlined
                color="primary"
                class="ma-2"
                @click="scheduleDemo"
              >
                <v-icon left>mdi-calendar</v-icon>
                Agendar Demo
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </div>
</template>

<script>
export default {
  name: 'DemoPage',
  data() {
    return {
      demoHighlights: [
        {
          icon: 'mdi-lightning-bolt',
          title: 'Procesamiento Ultrarrápido',
          description: 'Facturas generadas en menos de 2 segundos',
          color: 'orange'
        },
        {
          icon: 'mdi-qrcode-scan',
          title: 'Scanner Integrado',
          description: 'Escaneo de códigos QR y EAN en tiempo real',
          color: 'blue'
        },
        {
          icon: 'mdi-brain',
          title: 'IA Integrada',
          description: 'Claude AI para análisis y optimización automática',
          color: 'purple'
        },
        {
          icon: 'mdi-earth',
          title: 'Soporte Internacional',
          description: 'Adaptado para múltiples países y normativas',
          color: 'green'
        }
      ],

      systemFeatures: [
        {
          title: 'Punto de Venta (POS)',
          description: 'Interfaz intuitiva para procesamiento rápido de ventas con scanner de códigos, gestión de productos y múltiples métodos de pago.',
          features: ['Scanner QR/EAN', 'Múltiples pagos', 'Cliente express', 'Impresión automática'],
          icon: 'mdi-cash-register',
          color: 'green',
          route: '/PLU'
        },
        {
          title: 'Facturación Electrónica',
          description: 'Sistema completo de facturación electrónica con validación en tiempo real y cumplimiento normativo internacional.',
          features: ['Validación DIAN/AEAT', 'Firmado digital', 'Envío automático', 'Trazabilidad completa'],
          icon: 'mdi-receipt',
          color: 'blue',
          route: '/InvoiceE'
        },
        {
          title: 'Gestión de Inventario',
          description: 'Control total del stock con seguimiento en tiempo real, alertas automáticas y reportes de rotación.',
          features: ['Stock en tiempo real', 'Alertas automáticas', 'Multi-almacén', 'Rotación ABC'],
          icon: 'mdi-warehouse',
          color: 'orange',
          route: '/Inventory'
        },
        {
          title: 'Inteligencia Artificial',
          description: 'Asistente IA integrado para análisis de datos, optimización de precios y insights de negocio automatizados.',
          features: ['Análisis predictivo', 'Optimización precios', 'Insights automáticos', 'Chat inteligente'],
          icon: 'mdi-brain',
          color: 'purple',
          route: '/aibos'
        }
      ],

      demoModules: [
        { 
          name: 'POS', 
          icon: 'mdi-cash-register', 
          color: 'green', 
          route: '/PLU',
          description: 'Punto de venta completo'
        },
        { 
          name: 'Facturación', 
          icon: 'mdi-receipt', 
          color: 'blue', 
          route: '/InvoiceE',
          description: 'Facturas electrónicas'
        },
        { 
          name: 'Inventario', 
          icon: 'mdi-warehouse', 
          color: 'orange', 
          route: '/Inventory',
          description: 'Control de stock'
        },
        { 
          name: 'Clientes', 
          icon: 'mdi-account-group', 
          color: 'purple', 
          route: '/Clients',
          description: 'Gestión de clientes'
        },
        { 
          name: 'Reportes', 
          icon: 'mdi-chart-line', 
          color: 'red', 
          route: '/Resume',
          description: 'Analytics y KPIs'
        },
        { 
          name: 'IA Assistant', 
          icon: 'mdi-robot', 
          color: 'indigo', 
          route: '/aibos',
          description: 'Asistente inteligente'
        }
      ],

      competitiveAdvantages: [
        {
          icon: 'mdi-rocket-launch',
          title: 'Implementación Rápida',
          description: 'Sistema listo para usar en menos de 24 horas. Sin complicaciones técnicas ni largos procesos de configuración.',
          color: 'orange'
        },
        {
          icon: 'mdi-cash-multiple',
          title: 'ROI Inmediato',
          description: 'Recupera tu inversión desde el primer mes con la optimización de procesos y reducción de errores.',
          color: 'green'
        },
        {
          icon: 'mdi-shield-check',
          title: 'Cumplimiento Normativo',
          description: 'Validación automática con entes fiscales internacionales. Siempre actualizado con los últimos cambios normativos.',
          color: 'blue'
        }
      ]
    }
  },

  methods: {
    navigateTo(route) {
      this.$router.push(route)
    },

    scrollToSection(sectionId) {
      this.$vuetify.goTo(`#${sectionId}`, {
        duration: 800,
        offset: 0,
        easing: 'easeInOutCubic'
      })
    },

    startLiveDemo() {
      this.$router.push('/PLU')
    },

    scheduleDemo() {
      window.open('mailto:demo@ai-pos.com?subject=Solicitud de Demo AI-POS&body=Hola,%0A%0AMe gustaría agendar una demo personalizada de AI-POS.%0A%0ATipo de negocio:%0ANúmero de tiendas:%0ATeléfono de contacto:%0A%0AGracias', '_blank')
    },

    contactSales() {
      window.open('mailto:ventas@ai-pos.com?subject=Consulta sobre AI-POS ERP&body=Hola,%0A%0AMe interesa conocer más sobre AI-POS ERP para mi empresa.%0A%0AGracias', '_blank')
    }
  }
}
</script>

<style scoped>
.hero-demo {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.demo-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
}

.demo-mockup {
  padding: 0;
  background: #f5f5f5;
  min-height: 400px;
}

.mockup-header {
  background: #1976d2;
  color: white;
  padding: 15px 20px;
  font-weight: bold;
}

.mockup-content {
  padding: 20px;
}

.mockup-stats {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.stat-card {
  flex: 1;
  min-width: 120px;
  padding: 15px;
  border-radius: 8px;
  text-align: center;
  color: white;
}

.stat-card.green { background: #4caf50; }
.stat-card.blue { background: #2196f3; }
.stat-card.orange { background: #ff9800; }

.stat-title {
  font-size: 12px;
  opacity: 0.9;
  margin-bottom: 5px;
}

.stat-value {
  font-size: 20px;
  font-weight: bold;
}

.mockup-table {
  background: white;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #e0e0e0;
}

.table-header {
  font-weight: bold;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.table-row {
  padding: 8px 0;
  font-size: 12px;
  color: #666;
  border-bottom: 1px solid #f0f0f0;
}

.table-row:last-child {
  border-bottom: none;
}

.feature-demo-card {
  transition: all 0.3s ease;
}

.feature-demo-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
}

.demo-interactive {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.demo-module-card {
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
}

.demo-module-card:hover {
  transform: scale(1.05);
  box-shadow: 0 8px 25px rgba(0,0,0,0.3) !important;
}

.h-100 {
  height: 100%;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .mockup-stats {
    flex-direction: column;
  }
  
  .stat-card {
    min-width: 100%;
  }
  
  .display-1 {
    font-size: 1.75rem !important;
  }
}
</style>
